import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { Box, LinearProgress } from '@mui/material'
import { ui } from '../../../utils/uiTokens'
import { CRM_POLL, CrmTrackView, crmApi } from '../../../features/crm/crmApi'
import { Card, CardHead, CrmTimer, Empty, Hint, InfoBox, Lightbox, Muted, PageHead, Pill, TONES, contentSx, pageSx } from '../shared/crmUi'
import { STEP_NOS, courseTitle, dictLabel, digits, formatPhone, hm, initials, subjectTitle } from '../shared/format'
import { avatar } from '../moderator/IntakesPage'
import { mentorColumn, trackDone } from '../shared/stages'
import StepCard from './StepCard'

const COLS = ['todo', 'doing', 'done'] as const

const MentorTasksPage = () => {
    const { formatMessage } = useIntl()
    const { data: tracks = [], isFetching } = crmApi.useGetMentorTracksQuery(undefined, { pollingInterval: CRM_POLL })
    const [selected, setSelected] = useState(0)
    const [zoom, setZoom] = useState<string[] | null>(null)
    const detailRef = useRef<HTMLDivElement>(null)

    const sorted = useMemo(() => [...tracks].sort((a, b) => ((a.assignedAt || '') < (b.assignedAt || '') ? -1 : 1)), [tracks])
    const current = sorted.find((t) => t.id === selected) || sorted.find((t) => mentorColumn(t) !== 'done') || sorted[0]
    const multiGroup = new Set(tracks.map((t) => t.groupTitle)).size > 1

    const [scroll, setScroll] = useState(false)
    useEffect(() => {
        if (scroll && window.innerWidth < 900) detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        setScroll(false)
    }, [selected])

    return (
        <Box sx={pageSx}>
            <Box sx={contentSx}>
                <PageHead title={formatMessage({ id: 'crmTasks' })} sub={formatMessage({ id: 'crmTasksSub' })} />
                {isFetching && !tracks.length && <LinearProgress sx={{ marginBottom: 1 }} />}
                <MentorStrip tracks={tracks} />

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' }, gap: 2, marginBottom: '16px' }}>
                    {COLS.map((col) => {
                        const items = sorted.filter((t) => mentorColumn(t) === col)
                        return (
                            <Box key={col} sx={{ background: ui.surface, border: `1px solid ${ui.border}`, borderRadius: '14px', padding: '10px', minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, padding: '4px 6px 10px' }}>
                                    <b style={{ fontSize: 14 }}>{formatMessage({ id: `crmCol_${col}` })}</b>
                                    <Muted>{formatMessage({ id: `crmColEn_${col}` })}</Muted>
                                    <Pill tone={col === 'done' ? 'ok' : col === 'doing' ? 'wait' : 'grey'} sx={{ marginLeft: 'auto' }}>
                                        {items.length}
                                    </Pill>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {!items.length && <Box sx={{ color: ui.muted, fontSize: '13px', textAlign: 'center', padding: '16px' }}>{formatMessage({ id: 'crmEmptyCol' })}</Box>}
                                    {items.map((t) => (
                                        <KanbanCard
                                            key={t.id}
                                            t={t}
                                            multiGroup={multiGroup}
                                            selected={current?.id === t.id}
                                            onClick={() => {
                                                setScroll(true)
                                                setSelected(t.id)
                                            }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )
                    })}
                </Box>

                <Box ref={detailRef} sx={{ scrollMarginTop: '12px' }}>
                    {current ? (
                        <Card>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', marginBottom: '12px' }}>
                                <Box sx={{ minWidth: 0 }}>
                                    <Muted>{formatMessage({ id: 'crmHeadAssignedAt' }, { time: hm(current.assignedAt) })}</Muted>
                                    <Box sx={{ fontSize: '20px', fontWeight: 800, overflowWrap: 'anywhere' }}>{current.studentName}</Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
                                    <CrmTimer deadlineAt={current.mentorDeadlineAt} closedAt={current.finishedAt} big />
                                    <Muted>
                                        {formatMessage({ id: 'crmDeadlineAt' }, { time: hm(current.mentorDeadlineAt) })} · {current.doneCount}/5
                                    </Muted>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'minmax(240px, 320px) minmax(0, 1fr)' }, gap: 2, alignItems: 'start' }}>
                                <Box sx={{ position: { md: 'sticky' }, top: { md: 12 }, minWidth: 0 }}>
                                    <InfoBox
                                        title={formatMessage({ id: 'crmStudentAndParent' })}
                                        rows={[
                                            [formatMessage({ id: 'crmStudent' }), current.studentName],
                                            [
                                                formatMessage({ id: 'crmPhone' }),
                                                <>
                                                    {formatPhone(current.studentPhone)}
                                                    {(!current.login || digits(current.login) === digits(formatPhone(current.studentPhone))) && (
                                                        <Box component="span" sx={{ color: ui.muted }}> · {formatMessage({ id: 'crmPlatformLogin' })}</Box>
                                                    )}
                                                </>,
                                            ],
                                            ...(current.login && digits(current.login) !== digits(formatPhone(current.studentPhone))
                                                ? [[formatMessage({ id: 'crmLogin' }), current.login] as [string, React.ReactNode]]
                                                : []),
                                            [formatMessage({ id: 'crmGrade' }), dictLabel(formatMessage, 'Grade', current.grade)],
                                            [formatMessage({ id: 'crmLanguage' }), dictLabel(formatMessage, 'Lang', current.language)],
                                            [formatMessage({ id: 'crmParent' }), current.parentName],
                                            [formatMessage({ id: 'crmPhone' }), formatPhone(current.parentPhone)],
                                        ]}
                                    />
                                </Box>
                                <Box sx={{ minWidth: 0 }}>
                                    <Box sx={{ marginBottom: '10px' }}>
                                        <CardHead title={formatMessage({ id: 'crmTasks' })} />
                                        <Hint sx={{ marginTop: '-8px' }}>{formatMessage({ id: 'crmStepsHint' })}</Hint>
                                    </Box>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                        {STEP_NOS.map((no) => (
                                            <StepCard key={no} t={current} no={no} onZoom={setZoom} />
                                        ))}
                                    </Box>
                                </Box>
                            </Box>
                        </Card>
                    ) : (
                        !isFetching && (
                            <Card>
                                <Empty text={formatMessage({ id: 'crmNoTasks' })} />
                            </Card>
                        )
                    )}
                </Box>
                <Lightbox urls={zoom} onClose={() => setZoom(null)} />
            </Box>
        </Box>
    )
}

// «KZ-10 · 11 сынып» -> «KZ-10»
export const groupShort = (g?: string | null) => ((g || '').match(/(KZ|RU|ҚАЗ|РУС)-?\d+/i) || [g || '-'])[0]

export const GroupTag: React.FC<{ title?: string | null }> = ({ title }) => (
    <Box
        component="span"
        title={title || ''}
        sx={{ display: 'inline-block', padding: '1px 7px', borderRadius: '100px', background: ui.pinkSoft, color: ui.pink, fontSize: '11px', fontWeight: 700, verticalAlign: 'middle', whiteSpace: 'nowrap' }}
    >
        {groupShort(title)}
    </Box>
)

// Общая полоса над «Тапсырмалар» и «Оқушыларым»: кто ментор, какой предмет и курс, группы и сколько в них учеников
export const MentorStrip: React.FC<{ tracks: CrmTrackView[] }> = ({ tracks }) => {
    const { formatMessage, locale } = useIntl()
    if (!tracks.length) return null
    const name = tracks.find((t) => t.mentorName)?.mentorName || ''
    const subjects = Array.from(new Set(tracks.map((t) => subjectTitle(t.subject, locale)))).join(', ')
    const courses = Array.from(new Set(tracks.map((t) => courseTitle(t, locale)).filter(Boolean))).join(', ')
    const groups = new Map<string, number>()
    tracks.forEach((t) => t.groupTitle && groups.set(t.groupTitle, (groups.get(t.groupTitle) || 0) + 1))
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                flexWrap: 'wrap',
                border: `1px solid ${ui.border}`,
                borderRadius: '14px',
                padding: '10px 14px',
                marginBottom: '16px',
                background: ui.surface,
            }}
        >
            {name && <Box sx={avatar}>{initials(name)}</Box>}
            <Box sx={{ minWidth: 0, flex: '1 1 220px' }}>
                {name && <Box sx={{ fontWeight: 700 }}>{name}</Box>}
                <Muted sx={{ fontSize: '12.5px' }}>
                    {formatMessage({ id: 'crmSubject' })}: <b style={{ color: ui.ink }}>{subjects}</b>
                    {courses && (
                        <>
                            {' · '}
                            {formatMessage({ id: 'crmCourse' })}: <b style={{ color: ui.ink }}>{courses}</b>
                        </>
                    )}
                </Muted>
            </Box>
            <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {Array.from(groups.entries()).map(([g, n]) => (
                    <Pill key={g} tone="pink">
                        {g} · {n}
                    </Pill>
                ))}
            </Box>
        </Box>
    )
}

const KanbanCard: React.FC<{ t: CrmTrackView; selected: boolean; multiGroup: boolean; onClick: () => void }> = ({ t, selected, multiGroup, onClick }) => {
    const { formatMessage } = useIntl()
    const done = trackDone(t)
    const waiting = t.doneCount >= 5 && !done
    return (
        <Box
            component="button"
            type="button"
            onClick={onClick}
            sx={{
                textAlign: 'left',
                border: `1px solid ${selected ? ui.pink : ui.border}`,
                boxShadow: selected ? `0 0 0 2px ${ui.pinkSoft}` : 'none',
                background: ui.card,
                borderRadius: '12px',
                padding: '10px 12px',
                cursor: 'pointer',
                font: 'inherit',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                width: '100%',
            }}
        >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Box sx={{ fontWeight: 700, fontSize: '14px', color: ui.ink, overflowWrap: 'anywhere', minWidth: 0 }}>
                    {t.studentName} {multiGroup && <GroupTag title={t.groupTitle} />}
                </Box>
                {done ? (
                    <Pill tone="ok">✓ {formatMessage({ id: 'crmAllVerified' })}</Pill>
                ) : waiting ? (
                    <Pill tone="wait">⏳ {formatMessage({ id: 'crmUnderCheck' })}</Pill>
                ) : (
                    <CrmTimer deadlineAt={t.mentorDeadlineAt} closedAt={t.finishedAt} />
                )}
            </Box>
            {!done && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Box sx={{ display: 'flex', gap: '4px', flex: 1 }}>
                        {STEP_NOS.map((no) => {
                            const st = t.steps?.find((s) => s.stepNo === no)
                            return (
                                <Box
                                    key={no}
                                    sx={{ flex: 1, height: 6, borderRadius: 3, background: st?.verifiedAt ? TONES.ok.text : st?.doneAt ? TONES.wait.text : ui.track }}
                                />
                            )
                        })}
                    </Box>
                    <Muted sx={{ whiteSpace: 'nowrap' }}>
                        {t.doneCount}/5 · ✓{t.verifiedCount}
                    </Muted>
                </Box>
            )}
        </Box>
    )
}

export default MentorTasksPage
